import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { DevBoxes } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { DevCenterClient } from "../devCenterClient";
import {
  Pool,
  DevBoxesListPoolsNextOptionalParams,
  DevBoxesListPoolsOptionalParams,
  DevBoxesListPoolsResponse,
  Schedule,
  DevBoxesListSchedulesByProjectNextOptionalParams,
  DevBoxesListSchedulesByProjectOptionalParams,
  DevBoxesListSchedulesByProjectResponse,
  DevBoxesListSchedulesByPoolNextOptionalParams,
  DevBoxesListSchedulesByPoolOptionalParams,
  DevBoxesListSchedulesByPoolResponse,
  DevBox,
  DevBoxesListDevBoxesByUserNextOptionalParams,
  DevBoxesListDevBoxesByUserOptionalParams,
  DevBoxesListDevBoxesByUserResponse,
  CustomizationGroup,
  DevBoxesListCustomizationGroupsNextOptionalParams,
  DevBoxesListCustomizationGroupsOptionalParams,
  DevBoxesListCustomizationGroupsResponse,
  DevBoxAction,
  DevBoxesListActionsNextOptionalParams,
  DevBoxesListActionsOptionalParams,
  DevBoxesListActionsResponse,
  DevBoxActionDelayResult,
  DevBoxesDelayActionsNextOptionalParams,
  DevBoxesDelayActionsOptionalParams,
  DevBoxesDelayActionsResponse,
  DevBoxOperationUnion,
  DevBoxesListOperationsNextOptionalParams,
  DevBoxesListOperationsOptionalParams,
  DevBoxesListOperationsResponse,
  DevBoxesGetPoolOptionalParams,
  DevBoxesGetPoolResponse,
  DevBoxesGetScheduleByPoolOptionalParams,
  DevBoxesGetScheduleByPoolResponse,
  DevBoxesGetDevBoxByUserOptionalParams,
  DevBoxesGetDevBoxByUserResponse,
  DevBoxesCreateDevBoxOptionalParams,
  DevBoxesCreateDevBoxResponse,
  DevBoxesDeleteDevBoxOptionalParams,
  DevBoxesDeleteDevBoxResponse,
  DevBoxesStartDevBoxOptionalParams,
  DevBoxesStartDevBoxResponse,
  DevBoxesStopDevBoxOptionalParams,
  DevBoxesStopDevBoxResponse,
  DevBoxesRestartDevBoxOptionalParams,
  DevBoxesRestartDevBoxResponse,
  DevBoxesRepairDevBoxOptionalParams,
  DevBoxesRepairDevBoxResponse,
  DevBoxesGetCustomizationGroupOptionalParams,
  DevBoxesGetCustomizationGroupResponse,
  DevBoxesCreateCustomizationGroupOptionalParams,
  DevBoxesCreateCustomizationGroupResponse,
  DevBoxesGetCustomizationTaskLogOptionalParams,
  DevBoxesGetCustomizationTaskLogResponse,
  DevBoxesGetRemoteConnectionOptionalParams,
  DevBoxesGetRemoteConnectionResponse,
  DevBoxesGetActionOptionalParams,
  DevBoxesGetActionResponse,
  DevBoxesSkipActionOptionalParams,
  DevBoxesDelayActionOptionalParams,
  DevBoxesDelayActionResponse,
  DevBoxesGetOperationOptionalParams,
  DevBoxesGetOperationResponse,
  DevBoxesListPoolsNextResponse,
  DevBoxesListSchedulesByProjectNextResponse,
  DevBoxesListSchedulesByPoolNextResponse,
  DevBoxesListDevBoxesByUserNextResponse,
  DevBoxesListCustomizationGroupsNextResponse,
  DevBoxesListActionsNextResponse,
  DevBoxesDelayActionsNextResponse,
  DevBoxesListOperationsNextResponse,
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing DevBoxes operations. */
export class DevBoxesImpl implements DevBoxes {
  private readonly client: DevCenterClient;

  /**
   * Initialize a new instance of the class DevBoxes class.
   * @param client Reference to the service client
   */
  constructor(client: DevCenterClient) {
    this.client = client;
  }

  /**
   * Lists available pools
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  public listPools(
    projectName: string,
    options?: DevBoxesListPoolsOptionalParams,
  ): PagedAsyncIterableIterator<Pool> {
    const iter = this.listPoolsPagingAll(projectName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPoolsPagingPage(projectName, options, settings);
      },
    };
  }

  private async *listPoolsPagingPage(
    projectName: string,
    options?: DevBoxesListPoolsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Pool[]> {
    let result: DevBoxesListPoolsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listPools(projectName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listPoolsNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPoolsPagingAll(
    projectName: string,
    options?: DevBoxesListPoolsOptionalParams,
  ): AsyncIterableIterator<Pool> {
    for await (const page of this.listPoolsPagingPage(projectName, options)) {
      yield* page;
    }
  }

  /**
   * Lists all schedules within a project that are configured by your project administrator.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  public listSchedulesByProject(
    projectName: string,
    options?: DevBoxesListSchedulesByProjectOptionalParams,
  ): PagedAsyncIterableIterator<Schedule> {
    const iter = this.listSchedulesByProjectPagingAll(projectName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listSchedulesByProjectPagingPage(
          projectName,
          options,
          settings,
        );
      },
    };
  }

  private async *listSchedulesByProjectPagingPage(
    projectName: string,
    options?: DevBoxesListSchedulesByProjectOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Schedule[]> {
    let result: DevBoxesListSchedulesByProjectResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listSchedulesByProject(projectName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listSchedulesByProjectNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listSchedulesByProjectPagingAll(
    projectName: string,
    options?: DevBoxesListSchedulesByProjectOptionalParams,
  ): AsyncIterableIterator<Schedule> {
    for await (const page of this.listSchedulesByProjectPagingPage(
      projectName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all schedules within a pool that are configured by your project administrator.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param poolName The name of a pool of Dev Boxes.
   * @param options The options parameters.
   */
  public listSchedulesByPool(
    projectName: string,
    poolName: string,
    options?: DevBoxesListSchedulesByPoolOptionalParams,
  ): PagedAsyncIterableIterator<Schedule> {
    const iter = this.listSchedulesByPoolPagingAll(
      projectName,
      poolName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listSchedulesByPoolPagingPage(
          projectName,
          poolName,
          options,
          settings,
        );
      },
    };
  }

  private async *listSchedulesByPoolPagingPage(
    projectName: string,
    poolName: string,
    options?: DevBoxesListSchedulesByPoolOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Schedule[]> {
    let result: DevBoxesListSchedulesByPoolResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listSchedulesByPool(projectName, poolName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listSchedulesByPoolNext(
        projectName,
        poolName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listSchedulesByPoolPagingAll(
    projectName: string,
    poolName: string,
    options?: DevBoxesListSchedulesByPoolOptionalParams,
  ): AsyncIterableIterator<Schedule> {
    for await (const page of this.listSchedulesByPoolPagingPage(
      projectName,
      poolName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists Dev Boxes in the project for a particular user.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  public listDevBoxesByUser(
    projectName: string,
    userId: string,
    options?: DevBoxesListDevBoxesByUserOptionalParams,
  ): PagedAsyncIterableIterator<DevBox> {
    const iter = this.listDevBoxesByUserPagingAll(projectName, userId, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listDevBoxesByUserPagingPage(
          projectName,
          userId,
          options,
          settings,
        );
      },
    };
  }

  private async *listDevBoxesByUserPagingPage(
    projectName: string,
    userId: string,
    options?: DevBoxesListDevBoxesByUserOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBox[]> {
    let result: DevBoxesListDevBoxesByUserResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listDevBoxesByUser(projectName, userId, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listDevBoxesByUserNext(
        projectName,
        userId,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listDevBoxesByUserPagingAll(
    projectName: string,
    userId: string,
    options?: DevBoxesListDevBoxesByUserOptionalParams,
  ): AsyncIterableIterator<DevBox> {
    for await (const page of this.listDevBoxesByUserPagingPage(
      projectName,
      userId,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists customization groups on the Dev Box. Listed customization groups exclude task information
   * unless specified via the include parameter.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  public listCustomizationGroups(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListCustomizationGroupsOptionalParams,
  ): PagedAsyncIterableIterator<CustomizationGroup> {
    const iter = this.listCustomizationGroupsPagingAll(
      projectName,
      userId,
      devBoxName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listCustomizationGroupsPagingPage(
          projectName,
          userId,
          devBoxName,
          options,
          settings,
        );
      },
    };
  }

  private async *listCustomizationGroupsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListCustomizationGroupsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<CustomizationGroup[]> {
    let result: DevBoxesListCustomizationGroupsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listCustomizationGroups(
        projectName,
        userId,
        devBoxName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listCustomizationGroupsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listCustomizationGroupsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListCustomizationGroupsOptionalParams,
  ): AsyncIterableIterator<CustomizationGroup> {
    for await (const page of this.listCustomizationGroupsPagingPage(
      projectName,
      userId,
      devBoxName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists actions on a Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  public listActions(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListActionsOptionalParams,
  ): PagedAsyncIterableIterator<DevBoxAction> {
    const iter = this.listActionsPagingAll(
      projectName,
      userId,
      devBoxName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listActionsPagingPage(
          projectName,
          userId,
          devBoxName,
          options,
          settings,
        );
      },
    };
  }

  private async *listActionsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListActionsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBoxAction[]> {
    let result: DevBoxesListActionsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listActions(
        projectName,
        userId,
        devBoxName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listActionsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listActionsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListActionsOptionalParams,
  ): AsyncIterableIterator<DevBoxAction> {
    for await (const page of this.listActionsPagingPage(
      projectName,
      userId,
      devBoxName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Delays all actions.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param until The time to delay the Dev Box action or actions until.
   * @param options The options parameters.
   */
  public listDelayActions(
    projectName: string,
    userId: string,
    devBoxName: string,
    until: Date,
    options?: DevBoxesDelayActionsOptionalParams,
  ): PagedAsyncIterableIterator<DevBoxActionDelayResult> {
    const iter = this.delayActionsPagingAll(
      projectName,
      userId,
      devBoxName,
      until,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.delayActionsPagingPage(
          projectName,
          userId,
          devBoxName,
          until,
          options,
          settings,
        );
      },
    };
  }

  private async *delayActionsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    until: Date,
    options?: DevBoxesDelayActionsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBoxActionDelayResult[]> {
    let result: DevBoxesDelayActionsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._delayActions(
        projectName,
        userId,
        devBoxName,
        until,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._delayActionsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *delayActionsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    until: Date,
    options?: DevBoxesDelayActionsOptionalParams,
  ): AsyncIterableIterator<DevBoxActionDelayResult> {
    for await (const page of this.delayActionsPagingPage(
      projectName,
      userId,
      devBoxName,
      until,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists operations on the Dev Box which have occurred within the past 90 days.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  public listOperations(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListOperationsOptionalParams,
  ): PagedAsyncIterableIterator<DevBoxOperationUnion> {
    const iter = this.listOperationsPagingAll(
      projectName,
      userId,
      devBoxName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listOperationsPagingPage(
          projectName,
          userId,
          devBoxName,
          options,
          settings,
        );
      },
    };
  }

  private async *listOperationsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListOperationsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBoxOperationUnion[]> {
    let result: DevBoxesListOperationsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listOperations(
        projectName,
        userId,
        devBoxName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listOperationsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listOperationsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListOperationsOptionalParams,
  ): AsyncIterableIterator<DevBoxOperationUnion> {
    for await (const page of this.listOperationsPagingPage(
      projectName,
      userId,
      devBoxName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists available pools
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  private _listPools(
    projectName: string,
    options?: DevBoxesListPoolsOptionalParams,
  ): Promise<DevBoxesListPoolsResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listPoolsOperationSpec,
    );
  }

  /**
   * Gets a pool
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param poolName The name of a pool of Dev Boxes.
   * @param options The options parameters.
   */
  getPool(
    projectName: string,
    poolName: string,
    options?: DevBoxesGetPoolOptionalParams,
  ): Promise<DevBoxesGetPoolResponse> {
    return this.client.sendOperationRequest(
      { projectName, poolName, options },
      getPoolOperationSpec,
    );
  }

  /**
   * Lists all schedules within a project that are configured by your project administrator.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  private _listSchedulesByProject(
    projectName: string,
    options?: DevBoxesListSchedulesByProjectOptionalParams,
  ): Promise<DevBoxesListSchedulesByProjectResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listSchedulesByProjectOperationSpec,
    );
  }

  /**
   * Lists all schedules within a pool that are configured by your project administrator.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param poolName The name of a pool of Dev Boxes.
   * @param options The options parameters.
   */
  private _listSchedulesByPool(
    projectName: string,
    poolName: string,
    options?: DevBoxesListSchedulesByPoolOptionalParams,
  ): Promise<DevBoxesListSchedulesByPoolResponse> {
    return this.client.sendOperationRequest(
      { projectName, poolName, options },
      listSchedulesByPoolOperationSpec,
    );
  }

  /**
   * Gets a schedule.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param poolName The name of a pool of Dev Boxes.
   * @param scheduleName The name of a schedule.
   * @param options The options parameters.
   */
  getScheduleByPool(
    projectName: string,
    poolName: string,
    scheduleName: string,
    options?: DevBoxesGetScheduleByPoolOptionalParams,
  ): Promise<DevBoxesGetScheduleByPoolResponse> {
    return this.client.sendOperationRequest(
      { projectName, poolName, scheduleName, options },
      getScheduleByPoolOperationSpec,
    );
  }

  /**
   * Lists Dev Boxes in the project for a particular user.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  private _listDevBoxesByUser(
    projectName: string,
    userId: string,
    options?: DevBoxesListDevBoxesByUserOptionalParams,
  ): Promise<DevBoxesListDevBoxesByUserResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, options },
      listDevBoxesByUserOperationSpec,
    );
  }

  /**
   * Gets a Dev Box
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  getDevBoxByUser(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesGetDevBoxByUserOptionalParams,
  ): Promise<DevBoxesGetDevBoxByUserResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      getDevBoxByUserOperationSpec,
    );
  }

  /**
   * Creates or replaces a Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param body Represents a environment.
   * @param options The options parameters.
   */
  createDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    body: DevBox,
    options?: DevBoxesCreateDevBoxOptionalParams,
  ): Promise<DevBoxesCreateDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, body, options },
      createDevBoxOperationSpec,
    );
  }

  /**
   * Deletes a Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  deleteDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesDeleteDevBoxOptionalParams,
  ): Promise<DevBoxesDeleteDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      deleteDevBoxOperationSpec,
    );
  }

  /**
   * Starts a Dev Box
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  startDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesStartDevBoxOptionalParams,
  ): Promise<DevBoxesStartDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      startDevBoxOperationSpec,
    );
  }

  /**
   * Stops a Dev Box
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  stopDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesStopDevBoxOptionalParams,
  ): Promise<DevBoxesStopDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      stopDevBoxOperationSpec,
    );
  }

  /**
   * Restarts a Dev Box
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  restartDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesRestartDevBoxOptionalParams,
  ): Promise<DevBoxesRestartDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      restartDevBoxOperationSpec,
    );
  }

  /**
   * Attempts automated repair steps to resolve common problems on a Dev Box. The Dev Box may restart
   * during this operation.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  repairDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesRepairDevBoxOptionalParams,
  ): Promise<DevBoxesRepairDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      repairDevBoxOperationSpec,
    );
  }

  /**
   * Lists customization groups on the Dev Box. Listed customization groups exclude task information
   * unless specified via the include parameter.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  private _listCustomizationGroups(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListCustomizationGroupsOptionalParams,
  ): Promise<DevBoxesListCustomizationGroupsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      listCustomizationGroupsOperationSpec,
    );
  }

  /**
   * Gets a customization group.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param customizationGroupName A customization group name.
   * @param options The options parameters.
   */
  getCustomizationGroup(
    projectName: string,
    userId: string,
    devBoxName: string,
    customizationGroupName: string,
    options?: DevBoxesGetCustomizationGroupOptionalParams,
  ): Promise<DevBoxesGetCustomizationGroupResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, customizationGroupName, options },
      getCustomizationGroupOperationSpec,
    );
  }

  /**
   * Applies customizations to the Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param customizationGroupName A customization group name.
   * @param body Represents a customization group.
   * @param options The options parameters.
   */
  createCustomizationGroup(
    projectName: string,
    userId: string,
    devBoxName: string,
    customizationGroupName: string,
    body: CustomizationGroup,
    options?: DevBoxesCreateCustomizationGroupOptionalParams,
  ): Promise<DevBoxesCreateCustomizationGroupResponse> {
    return this.client.sendOperationRequest(
      {
        projectName,
        userId,
        devBoxName,
        customizationGroupName,
        body,
        options,
      },
      createCustomizationGroupOperationSpec,
    );
  }

  /**
   * Gets the log for a customization task.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param customizationGroupName A customization group name.
   * @param customizationTaskId A customization task ID.
   * @param options The options parameters.
   */
  getCustomizationTaskLog(
    projectName: string,
    userId: string,
    devBoxName: string,
    customizationGroupName: string,
    customizationTaskId: string,
    options?: DevBoxesGetCustomizationTaskLogOptionalParams,
  ): Promise<DevBoxesGetCustomizationTaskLogResponse> {
    return this.client.sendOperationRequest(
      {
        projectName,
        userId,
        devBoxName,
        customizationGroupName,
        customizationTaskId,
        options,
      },
      getCustomizationTaskLogOperationSpec,
    );
  }

  /**
   * Gets RDP Connection info
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  getRemoteConnection(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesGetRemoteConnectionOptionalParams,
  ): Promise<DevBoxesGetRemoteConnectionResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      getRemoteConnectionOperationSpec,
    );
  }

  /**
   * Lists actions on a Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  private _listActions(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListActionsOptionalParams,
  ): Promise<DevBoxesListActionsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      listActionsOperationSpec,
    );
  }

  /**
   * Gets an action.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param actionName The name of an action that will take place on a Dev Box.
   * @param options The options parameters.
   */
  getAction(
    projectName: string,
    userId: string,
    devBoxName: string,
    actionName: string,
    options?: DevBoxesGetActionOptionalParams,
  ): Promise<DevBoxesGetActionResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, actionName, options },
      getActionOperationSpec,
    );
  }

  /**
   * Skips an occurrence of an action.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param actionName The name of an action that will take place on a Dev Box.
   * @param options The options parameters.
   */
  skipAction(
    projectName: string,
    userId: string,
    devBoxName: string,
    actionName: string,
    options?: DevBoxesSkipActionOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, actionName, options },
      skipActionOperationSpec,
    );
  }

  /**
   * Delays the occurrence of an action.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param actionName The name of an action that will take place on a Dev Box.
   * @param until The time to delay the Dev Box action or actions until.
   * @param options The options parameters.
   */
  delayAction(
    projectName: string,
    userId: string,
    devBoxName: string,
    actionName: string,
    until: Date,
    options?: DevBoxesDelayActionOptionalParams,
  ): Promise<DevBoxesDelayActionResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, actionName, until, options },
      delayActionOperationSpec,
    );
  }

  /**
   * Delays all actions.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param until The time to delay the Dev Box action or actions until.
   * @param options The options parameters.
   */
  private _delayActions(
    projectName: string,
    userId: string,
    devBoxName: string,
    until: Date,
    options?: DevBoxesDelayActionsOptionalParams,
  ): Promise<DevBoxesDelayActionsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, until, options },
      delayActionsOperationSpec,
    );
  }

  /**
   * Lists operations on the Dev Box which have occurred within the past 90 days.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  private _listOperations(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListOperationsOptionalParams,
  ): Promise<DevBoxesListOperationsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      listOperationsOperationSpec,
    );
  }

  /**
   * Gets an operation on a Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param operationId The id of the operation on a Dev Box.
   * @param options The options parameters.
   */
  getOperation(
    projectName: string,
    userId: string,
    devBoxName: string,
    operationId: string,
    options?: DevBoxesGetOperationOptionalParams,
  ): Promise<DevBoxesGetOperationResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, operationId, options },
      getOperationOperationSpec,
    );
  }

  /**
   * ListPoolsNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param nextLink The nextLink from the previous successful call to the ListPools method.
   * @param options The options parameters.
   */
  private _listPoolsNext(
    projectName: string,
    nextLink: string,
    options?: DevBoxesListPoolsNextOptionalParams,
  ): Promise<DevBoxesListPoolsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listPoolsNextOperationSpec,
    );
  }

  /**
   * ListSchedulesByProjectNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param nextLink The nextLink from the previous successful call to the ListSchedulesByProject method.
   * @param options The options parameters.
   */
  private _listSchedulesByProjectNext(
    projectName: string,
    nextLink: string,
    options?: DevBoxesListSchedulesByProjectNextOptionalParams,
  ): Promise<DevBoxesListSchedulesByProjectNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listSchedulesByProjectNextOperationSpec,
    );
  }

  /**
   * ListSchedulesByPoolNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param poolName The name of a pool of Dev Boxes.
   * @param nextLink The nextLink from the previous successful call to the ListSchedulesByPool method.
   * @param options The options parameters.
   */
  private _listSchedulesByPoolNext(
    projectName: string,
    poolName: string,
    nextLink: string,
    options?: DevBoxesListSchedulesByPoolNextOptionalParams,
  ): Promise<DevBoxesListSchedulesByPoolNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, poolName, nextLink, options },
      listSchedulesByPoolNextOperationSpec,
    );
  }

  /**
   * ListDevBoxesByUserNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param nextLink The nextLink from the previous successful call to the ListDevBoxesByUser method.
   * @param options The options parameters.
   */
  private _listDevBoxesByUserNext(
    projectName: string,
    userId: string,
    nextLink: string,
    options?: DevBoxesListDevBoxesByUserNextOptionalParams,
  ): Promise<DevBoxesListDevBoxesByUserNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, nextLink, options },
      listDevBoxesByUserNextOperationSpec,
    );
  }

  /**
   * ListCustomizationGroupsNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param nextLink The nextLink from the previous successful call to the ListCustomizationGroups
   *                 method.
   * @param options The options parameters.
   */
  private _listCustomizationGroupsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesListCustomizationGroupsNextOptionalParams,
  ): Promise<DevBoxesListCustomizationGroupsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      listCustomizationGroupsNextOperationSpec,
    );
  }

  /**
   * ListActionsNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param nextLink The nextLink from the previous successful call to the ListActions method.
   * @param options The options parameters.
   */
  private _listActionsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesListActionsNextOptionalParams,
  ): Promise<DevBoxesListActionsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      listActionsNextOperationSpec,
    );
  }

  /**
   * DelayActionsNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param nextLink The nextLink from the previous successful call to the DelayActions method.
   * @param options The options parameters.
   */
  private _delayActionsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesDelayActionsNextOptionalParams,
  ): Promise<DevBoxesDelayActionsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      delayActionsNextOperationSpec,
    );
  }

  /**
   * ListOperationsNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param nextLink The nextLink from the previous successful call to the ListOperations method.
   * @param options The options parameters.
   */
  private _listOperationsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesListOperationsNextOptionalParams,
  ): Promise<DevBoxesListOperationsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      listOperationsNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listPoolsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/pools",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PoolListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListPoolsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [Parameters.endpoint, Parameters.projectName1],
  headerParameters: [Parameters.accept],
  serializer,
};
const getPoolOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/pools/{poolName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Pool,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesGetPoolExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName1,
    Parameters.poolName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSchedulesByProjectOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/schedules",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ScheduleListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListSchedulesByProjectExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [Parameters.endpoint, Parameters.projectName1],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSchedulesByPoolOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/pools/{poolName}/schedules",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ScheduleListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListSchedulesByPoolExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName1,
    Parameters.poolName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getScheduleByPoolOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/pools/{poolName}/schedules/{scheduleName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Schedule,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesGetScheduleByPoolExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName1,
    Parameters.poolName,
    Parameters.scheduleName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listDevBoxesByUserOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListDevBoxesByUserExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getDevBoxByUserOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBox,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesGetDevBoxByUserExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.DevBox,
    },
    201: {
      bodyMapper: Mappers.DevBox,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesCreateDevBoxExceptionHeaders,
    },
  },
  requestBody: Parameters.body1,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}",
  httpMethod: "DELETE",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesDeleteDevBoxHeaders,
    },
    204: {},
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesDeleteDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const startDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:start",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesStartDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesStartDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const stopDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:stop",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesStopDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesStopDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.hibernate],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const restartDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:restart",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesRestartDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesRestartDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const repairDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:repair",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesRepairDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesRepairDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCustomizationGroupsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/customizationGroups",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CustomizationGroupListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListCustomizationGroupsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.include],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getCustomizationGroupOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/customizationGroups/{customizationGroupName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CustomizationGroup,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesGetCustomizationGroupExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
    Parameters.customizationGroupName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createCustomizationGroupOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/customizationGroups/{customizationGroupName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.CustomizationGroup,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesCreateCustomizationGroupExceptionHeaders,
    },
  },
  requestBody: Parameters.body2,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
    Parameters.customizationGroupName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getCustomizationTaskLogOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/customizationGroups/{customizationGroupName}/logs/{customizationTaskId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: { type: { name: "Stream" } },
    },
    default: {
      headersMapper: Mappers.DevBoxesGetCustomizationTaskLogExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
    Parameters.customizationGroupName,
    Parameters.customizationTaskId,
  ],
  headerParameters: [Parameters.accept1],
  serializer,
};
const getRemoteConnectionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/remoteConnection",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RemoteConnection,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesGetRemoteConnectionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listActionsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxActionsListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListActionsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions/{actionName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxAction,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesGetActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
    Parameters.actionName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const skipActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions/{actionName}:skip",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesSkipActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
    Parameters.actionName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const delayActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions/{actionName}:delay",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxAction,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesDelayActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.until],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
    Parameters.actionName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const delayActionsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions:delay",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxActionsDelayMultipleResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesDelayActionsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.until],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/operations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxOperationListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListOperationsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.filter, Parameters.top],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/operations/{operationId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxOperation,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesGetOperationExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.devBoxName,
    Parameters.operationId,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listPoolsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PoolListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListPoolsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName1,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSchedulesByProjectNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ScheduleListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListSchedulesByProjectNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName1,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSchedulesByPoolNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ScheduleListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListSchedulesByPoolNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName1,
    Parameters.nextLink,
    Parameters.poolName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listDevBoxesByUserNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListDevBoxesByUserNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCustomizationGroupsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CustomizationGroupListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper:
        Mappers.DevBoxesListCustomizationGroupsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.nextLink,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listActionsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxActionsListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListActionsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.nextLink,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const delayActionsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxActionsDelayMultipleResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesDelayActionsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.nextLink,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxOperationListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
      headersMapper: Mappers.DevBoxesListOperationsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.userId,
    Parameters.projectName1,
    Parameters.nextLink,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
